/** "New lead" dialog (F3-I) — manual entry from the kanban (walk-in/call). */
import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import { UserPlus } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { MANUAL_SOURCES, useCreateLead } from "./api";
import { SOURCE_LABEL } from "./labels";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org?: string;
}

export function NewLeadDialog(props: Props) {
  const { t } = useI18n();
  const create = useCreateLead();
  const [name, setName] = createSignal("");
  const [phone, setPhone] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [source, setSource] = createSignal<string>("walk_in");

  const submit = (e: Event) => {
    e.preventDefault();
    if (!name().trim() || !phone().trim()) {
      showToast(t(msg`Name and phone are required`), "error");
      return;
    }
    create.mutate(
      {
        org: props.org,
        name: name().trim(),
        phone: phone().trim(),
        email: email().trim() || undefined,
        source: source(),
      },
      {
        onSuccess: () => {
          showToast(t(msg`Lead created`), "success");
          setName("");
          setPhone("");
          setEmail("");
          props.onOpenChange(false);
        },
        onError: (err) => showToast(err.message, "error"),
      },
    );
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <UserPlus class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">{t(msg`New lead`)}</DialogTitle>
        </div>
      </DialogHeader>
      <form onSubmit={submit} class="space-y-4 p-6" data-testid="crm-new-lead-form">
        <DialogDescription class="text-sm text-muted-foreground">
          {t(msg`Register a prospect who contacted the academy.`)}
        </DialogDescription>
        <div class="space-y-2">
          <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
            {t(msg`Name`)}
          </Label>
          <TextField
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            data-testid="crm-lead-name"
            autofocus
          />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-2">
            <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
              {t(msg`Phone`)}
            </Label>
            <TextField
              value={phone()}
              onInput={(e) => setPhone(e.currentTarget.value)}
              type="tel"
              data-testid="crm-lead-phone"
              placeholder="0414-1234567"
            />
          </div>
          <div class="space-y-2">
            <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
              {t(msg`Source`)}
            </Label>
            <Select
              options={MANUAL_SOURCES.map((s) => ({ value: s, label: t(SOURCE_LABEL[s]) }))}
              value={source()}
              onChange={setSource}
              aria-label={t(msg`Source`)}
            />
          </div>
        </div>
        <div class="space-y-2">
          <Label class="text-xs font-bold uppercase tracking-wider opacity-50">
            {t(msg`Email (optional)`)}
          </Label>
          <TextField
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
            type="email"
          />
        </div>
        <div class="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            class="h-11 rounded-xl px-6"
            onClick={() => props.onOpenChange(false)}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            type="submit"
            disabled={create.isPending}
            data-testid="crm-lead-submit"
            class="h-11 rounded-xl px-8 font-bold"
          >
            {t(msg`Create lead`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
